import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import moment from 'moment';
import { Box } from '@mui/material';

import Modal from 'modules/views/shared/Modal';
import Select from 'modules/views/shared/Select';
import TextField from 'modules/views/shared/TextField';
import DatePicker from 'modules/views/shared/DatePicker';
import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { createWorkerDrugTestingSchedule, updateWorkerDrugTestingSchedule } from 'modules/state-mgmt/workerData';
import { prettifyErrorResponse } from 'utils';

import { IDrugTestAppointment } from '../types';
import { useStylesDrugTestingModal } from '../styles';
import { useBookAppointmentOptions } from '../hooks/useBookAppointmentOptions';

export interface IBookAppointmentForm {
  projectId: string;
  facilityId: string;
  panelId: string;
  typeId: string;
  reason: string;
  appointmentDate: moment.Moment | null;
}

interface IBookAppointmentModalProps {
  isVisible: boolean;
  onClose: () => void;
  // Called after a successful booking/update so the caller can close + refresh the list.
  onConfirm: () => void;
  // When provided, the modal opens in edit mode prefilled from this appointment.
  appointment?: IDrugTestAppointment | null;
}

const INITIAL_FORM: IBookAppointmentForm = {
  projectId: '',
  facilityId: '',
  panelId: '',
  typeId: '',
  reason: '',
  appointmentDate: null,
};

// Ensures the currently-selected value is always present as an option (and thus displayed),
// even if it's been filtered out of / not yet loaded into the fetched list (used in edit mode).
const withSelectedOption = (
  options: Array<{ value: string; label: string; [key: string]: any }>,
  value?: string,
  label?: string,
) => {
  if (!value || options.some(option => option.value === value)) return options;
  return [{ value, label: label || '' }, ...options];
};

const BookAppointmentModal: React.FC<IBookAppointmentModalProps> = ({ isVisible, onClose, onConfirm, appointment }) => {
  const classes = useStylesDrugTestingModal();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const workerId = useAppSelector(state => state.onboarding.id || state.user?.workerId);

  const isEditMode = !!appointment;

  const [formData, setFormData] = useState<IBookAppointmentForm>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof IBookAppointmentForm, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    projects,
    facilities,
    panels,
    types,
    facilityInfo,
    invoiceToCompanyId,
    isLoadingProjects,
    isLoadingFacilities,
    isLoadingPanels,
    isLoadingTypes,
  } = useBookAppointmentOptions(formData.projectId, formData.facilityId, isVisible);

  // In edit mode, guarantee the appointment's current selections are always shown.
  const projectOptions = useMemo(
    () => withSelectedOption(projects, appointment?.projectId, appointment?.projectName),
    [projects, appointment],
  );
  const facilityOptions = useMemo(
    () => withSelectedOption(facilities, appointment?.facilityId, appointment?.facilityName),
    [facilities, appointment],
  );
  const panelOptions = useMemo(
    () => withSelectedOption(panels, appointment?.panelId, appointment?.panelName),
    [panels, appointment],
  );
  const typeOptions = useMemo(
    () => withSelectedOption(types, appointment?.drugTestingTypeId, appointment?.typeName),
    [types, appointment],
  );

  useEffect(() => {
    if (!isVisible) {
      setFormData(INITIAL_FORM);
      setErrors({});
      setIsSubmitting(false);
      return;
    }
    if (appointment) {
      setFormData({
        projectId: appointment.projectId || '',
        facilityId: appointment.facilityId || '',
        panelId: appointment.panelId || '',
        typeId: appointment.drugTestingTypeId || '',
        reason: appointment.reason && appointment.reason !== '--' ? appointment.reason : '',
        appointmentDate: appointment.testScheduledForDate ? moment(appointment.testScheduledForDate) : null,
      });
    }
  }, [isVisible, appointment]);

  const handleFieldChange = <K extends keyof IBookAppointmentForm>(field: K, value: IBookAppointmentForm[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Project drives the facility, type, and (via facility) panel lists — clear all dependents.
  const handleProjectChange = (projectId: string) => {
    setFormData(prev => ({ ...prev, projectId, facilityId: '', panelId: '', typeId: '' }));
    setErrors(prev => ({ ...prev, projectId: undefined }));
  };

  // Facility drives the panel list + facility-info card — changing it clears the selected panel.
  // In edit mode the panel is locked, so it's preserved.
  const handleFacilityChange = (facilityId: string) => {
    setFormData(prev => ({ ...prev, facilityId, panelId: isEditMode ? prev.panelId : '' }));
    setErrors(prev => ({ ...prev, facilityId: undefined }));
  };

  const validateForm = () => {
    const nextErrors: Partial<Record<keyof IBookAppointmentForm, string>> = {};

    if (!formData.projectId) nextErrors.projectId = t('myDrugTesting.bookAppointment.errors.projectRequired');
    if (!formData.facilityId) nextErrors.facilityId = t('myDrugTesting.bookAppointment.errors.facilityRequired');
    if (!formData.panelId) nextErrors.panelId = t('myDrugTesting.bookAppointment.errors.panelRequired');
    if (!formData.typeId) nextErrors.typeId = t('myDrugTesting.bookAppointment.errors.typeRequired');
    if (!formData.reason.trim()) nextErrors.reason = t('myDrugTesting.bookAppointment.errors.reasonRequired');
    if (!formData.appointmentDate) nextErrors.appointmentDate = t('myDrugTesting.bookAppointment.errors.dateRequired');

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleConfirm = () => {
    if (!validateForm()) return;

    const input = {
      workerId: workerId || '',
      invoiceToCompanyId,
      projectId: formData.projectId,
      facilityId: formData.facilityId,
      drugTestingTypeId: formData.typeId,
      // API expects a midnight, timezone-free timestamp e.g. "2026-01-22T00:00:00".
      testScheduledForDate: formData.appointmentDate
        ? formData.appointmentDate.startOf('day').format('YYYY-MM-DDTHH:mm:ss')
        : '',
      reason: formData.reason.trim(),
      panelIds: [formData.panelId],
    };

    const request =
      isEditMode && appointment
        ? dispatch(updateWorkerDrugTestingSchedule({ scheduleId: appointment.id, input }))
        : dispatch(createWorkerDrugTestingSchedule(input));

    setIsSubmitting(true);
    request
      .unwrap()
      .then(() => {
        const message = isEditMode
          ? t('myDrugTesting.bookAppointment.updateSuccessMessage')
          : t('myDrugTesting.bookAppointment.successMessage');
        enqueueSnackbar(message, { variant: 'success' });
        onConfirm();
      })
      .catch(error => {
        enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  // Block closing (backdrop, esc, X, cancel) while a booking is in flight.
  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const content = (
    <div className={classes.content}>
      <div className={classes.fieldContainer}>
        <Select
          inputLabel={t('myDrugTesting.bookAppointment.selectProject')}
          required={true}
          disabled={isEditMode || isLoadingProjects}
          loading={isLoadingProjects}
          options={projectOptions}
          selectedValue={formData.projectId}
          valueAsSelectedValue={true}
          error={errors.projectId}
          testId="book-appointment-project-select"
          onChange={value => handleProjectChange(value || '')}
        />
      </div>

      <div className={classes.fieldContainer}>
        <Select
          inputLabel={t('myDrugTesting.bookAppointment.selectFacility')}
          required={true}
          grouped={true}
          disabled={!formData.projectId || isLoadingFacilities}
          loading={isLoadingFacilities}
          options={facilityOptions}
          selectedValue={formData.facilityId}
          valueAsSelectedValue={true}
          error={errors.facilityId}
          testId="book-appointment-facility-select"
          onChange={value => handleFacilityChange(value || '')}
        />
      </div>

      <div className={classes.fieldContainer}>
        <Select
          inputLabel={t('myDrugTesting.bookAppointment.selectPanel')}
          required={true}
          disabled={isEditMode || !formData.facilityId || isLoadingPanels}
          loading={isLoadingPanels}
          options={panelOptions}
          selectedValue={formData.panelId}
          valueAsSelectedValue={true}
          error={errors.panelId}
          testId="book-appointment-panel-select"
          onChange={value => handleFieldChange('panelId', value || '')}
        />
      </div>

      <div className={classes.fieldContainer}>
        <Select
          inputLabel={t('myDrugTesting.bookAppointment.selectType')}
          required={true}
          disabled={!formData.projectId || isLoadingTypes}
          loading={isLoadingTypes}
          options={typeOptions}
          selectedValue={formData.typeId}
          valueAsSelectedValue={true}
          error={errors.typeId}
          testId="book-appointment-type-select"
          onChange={value => handleFieldChange('typeId', value || '')}
        />
      </div>

      <div className={classes.fieldContainer}>
        <TextField
          label={t('myDrugTesting.bookAppointment.reason')}
          placeholder={t('myDrugTesting.bookAppointment.reason')}
          value={formData.reason}
          error={errors.reason}
          testId="book-appointment-reason-input"
          onChange={e => handleFieldChange('reason', e.target.value)}
        />
      </div>

      <div className={classes.fieldContainer}>
        <DatePicker
          inputLabel={t('myDrugTesting.bookAppointment.selectDate')}
          required={true}
          value={formData.appointmentDate}
          error={errors.appointmentDate}
          minDate={moment()}
          testId="book-appointment-date-picker"
          onChange={value => handleFieldChange('appointmentDate', value || null)}
        />
      </div>

      {facilityInfo && (
        <Box className={classes.facilityInfoCard} data-testid="book-appointment-facility-info">
          <Box className={classes.facilityInfoRow}>
            <p className={classes.facilityInfoLabel}>{t('myDrugTesting.bookAppointment.facilityInformation')}</p>
            <p className={classes.facilityInfoValue}>{facilityInfo.name}</p>
          </Box>
          {facilityInfo.address && (
            <Box className={classes.facilityInfoRow}>
              <p className={classes.facilityInfoLabel}>{t('myDrugTesting.bookAppointment.facilityAddress')}</p>
              <p className={classes.facilityInfoValue}>{facilityInfo.address}</p>
            </Box>
          )}
          {facilityInfo.hoursOfOperation && (
            <Box className={classes.facilityInfoRow}>
              <p className={classes.facilityInfoLabel}>{t('myDrugTesting.bookAppointment.hoursOfOperation')}</p>
              <p className={classes.facilityInfoValue} style={{ whiteSpace: 'pre-line' }}>
                {facilityInfo.hoursOfOperation}
              </p>
            </Box>
          )}
        </Box>
      )}
    </div>
  );

  return (
    <Modal
      title={isEditMode ? t('myDrugTesting.bookAppointment.editTitle') : t('myDrugTesting.bookAppointment.title')}
      isVisible={isVisible}
      modalStyleClass={classes.modal}
      modalContentStyleClass={classes.modalContent}
      disableScrollLock={true}
      disableBackdropClose={isSubmitting}
      hideCloseIcon={isSubmitting}
      testId="book-appointment-modal"
      Content={content}
      actionButtons={[
        {
          label: t('common.cancel'),
          variant: 'outlined',
          styleClass: classes.cancelButton,
          testId: 'book-appointment-cancel-button',
          disabled: isSubmitting,
          onClick: handleClose,
        },
        {
          label: t('myDrugTesting.bookAppointment.confirm'),
          styleClass: classes.confirmButton,
          testId: 'book-appointment-confirm-button',
          isLoading: isSubmitting,
          onClick: handleConfirm,
        },
      ]}
      onClose={handleClose}
    />
  );
};

export default memo(BookAppointmentModal);
